import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { DischargeFormData } from '../../types/formData'
import { DischargeFormField } from '../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../helpers'

/**
 * Patient Overview subsection adapter
 * Handles patient demographics and discharge assessment status fields
 */
export class PatientOverviewAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const legalName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_LEGAL_NAME
      )?.at(0) || undefined
    const preferredName =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_PREFERRED_NAME
      )?.at(0) || undefined
    const dateOfBirth =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DATE_OF_BIRTH
      )?.at(0) || undefined
    const address =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_ADDRESS
      )?.at(0) || undefined
    const gender =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_GENDER
      )?.at(0) || undefined
    const sex =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_SEX
      )?.at(0) || undefined
    const medicaidId =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID
      )?.at(0) || undefined
    const orderingPractitioner =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_ORDERING_PRACTITIONER
      )?.at(0) || undefined
    const dischargeAssessmentStatus =
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DISCHARGE_ASSESSMENT_STATUS
      )?.at(0) || undefined

    return {
      [DischargeFormField.PATIENT_OVERVIEW_LEGAL_NAME]: legalName,
      [DischargeFormField.PATIENT_OVERVIEW_PREFERRED_NAME]: preferredName,
      [DischargeFormField.PATIENT_OVERVIEW_DATE_OF_BIRTH]: dateOfBirth,
      [DischargeFormField.PATIENT_OVERVIEW_ADDRESS]: address,
      [DischargeFormField.PATIENT_OVERVIEW_GENDER]: gender,
      [DischargeFormField.PATIENT_OVERVIEW_SEX]: sex,
      [DischargeFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID]: medicaidId,
      [DischargeFormField.PATIENT_OVERVIEW_ORDERING_PRACTITIONER]: orderingPractitioner,
      [DischargeFormField.PATIENT_OVERVIEW_DISCHARGE_ASSESSMENT_STATUS]: dischargeAssessmentStatus
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_LEGAL_NAME)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_LEGAL_NAME,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_LEGAL_NAME)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_PREFERRED_NAME)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_PREFERRED_NAME,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_PREFERRED_NAME)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_DATE_OF_BIRTH)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DATE_OF_BIRTH,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_DATE_OF_BIRTH)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_ADDRESS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_ADDRESS,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_ADDRESS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_GENDER)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_GENDER,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_GENDER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_SEX)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_SEX,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_SEX)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_DEMOGRAPHICS_MEDICAID_ID)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_ORDERING_PRACTITIONER)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_ORDERING_PRACTITIONER,
        value: this.getFormDataField(DischargeFormField.PATIENT_OVERVIEW_ORDERING_PRACTITIONER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.PATIENT_OVERVIEW_DISCHARGE_ASSESSMENT_STATUS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.PATIENT_OVERVIEW_DISCHARGE_ASSESSMENT_STATUS,
        value: this.getFormDataField(
          DischargeFormField.PATIENT_OVERVIEW_DISCHARGE_ASSESSMENT_STATUS
        )
      })
    }

    return mutations
  }
}
